import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, TrendingUp, Users, Mail, Briefcase, Activity,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { toast } from "sonner";
import { apiClient } from "../lib/site";
import { fadeUp } from "../components/sections";
import { getAdminToken, setAdminToken } from "./AdminLoginPage";

const ACCENT = "#C2533B";

function fmtPct(n) {
  return `${(n ?? 0).toFixed(1)}%`;
}

function shortDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
}

export default function AdminAnalyticsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = getAdminToken();
  const authHeader = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) {
      navigate("/admin", { replace: true });
      return;
    }
    Promise.all([
      apiClient.get("/admin/me", { headers: authHeader }),
      apiClient.get("/admin/analytics", { headers: authHeader }),
    ])
      .then(([meR, dataR]) => {
        setMe(meR.data);
        setData(dataR.data);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          setAdminToken(null);
          navigate("/admin", { replace: true });
        } else {
          toast.error("Couldn't load analytics.");
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || !data) {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center">
        <p className="text-zinc-500 text-sm">Loading analytics…</p>
      </div>
    );
  }

  const chartData = data.weeks.map((w) => ({
    week: shortDate(w.week_start),
    Leads: w.leads,
    Contacts: w.contacts,
    Applications: w.applications,
  }));

  const KPIS = [
    { label: "Lead Magnet Downloads", value: data.total_leads, icon: TrendingUp },
    { label: "Contact Inquiries", value: data.total_contacts, icon: Mail },
    { label: "Career Applications", value: data.total_applications, icon: Briefcase },
    {
      label: "Lead → Contact (7d)",
      value: fmtPct(data.conversion_rate),
      sub: `${data.converted_leads} of ${data.total_leads} leads`,
      icon: Activity,
    },
  ];

  return (
    <div data-testid="admin-analytics-page" className="min-h-[calc(100vh-80px)] px-6 md:px-12 lg:px-16 py-12 md:py-16">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 pb-10 border-b border-white/10">
        <div>
          <Link
            to="/admin/dashboard"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition text-sm mb-6"
            data-testid="analytics-back-link"
          >
            <ArrowLeft size={14} strokeWidth={1.5} /> Back to dashboard
          </Link>
          <p className="overline mb-3">Analytics</p>
          <h1 className="font-serif-display text-4xl md:text-5xl text-white font-light leading-tight">
            Funnel performance<span style={{ color: ACCENT }}>.</span>
          </h1>
          <p className="text-zinc-500 text-sm mt-3">
            {me?.email} · last refreshed {new Date(data.generated_at).toLocaleString("en-GB")}
          </p>
        </div>
      </div>

      {/* KPI cards */}
      <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#27272A] border border-[#27272A]">
        {KPIS.map((k, i) => {
          const Icon = k.icon;
          return (
            <motion.div
              key={i}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.06 }}
              className="bg-[#0A0A0C] p-8"
              data-testid={`kpi-${i}`}
            >
              <div className="flex items-center justify-between mb-6">
                <p className="overline" style={{ color: "#71717A" }}>{k.label}</p>
                <Icon size={16} strokeWidth={1.5} style={{ color: ACCENT }} />
              </div>
              <p
                className="font-serif-display text-5xl md:text-6xl font-light text-white"
                style={{ letterSpacing: "-0.03em" }}
              >
                {k.value}
              </p>
              {k.sub && <p className="text-zinc-500 text-xs mt-3">{k.sub}</p>}
            </motion.div>
          );
        })}
      </div>

      {/* Weekly chart */}
      <motion.div {...fadeUp} className="mt-12 border border-[#27272A] bg-[#0A0A0C] p-8" data-testid="weekly-chart">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="overline">12-Week Activity</p>
            <h2 className="font-serif-display text-2xl md:text-3xl text-white font-light mt-2">
              Inbound by week
            </h2>
          </div>
          <p className="text-zinc-500 text-xs hidden md:block">Mondays UTC</p>
        </div>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
              <XAxis
                dataKey="week"
                stroke="#71717A"
                tick={{ fontSize: 11, fill: "#A1A1AA" }}
                tickLine={false}
                axisLine={{ stroke: "#27272A" }}
              />
              <YAxis
                stroke="#71717A"
                tick={{ fontSize: 11, fill: "#A1A1AA" }}
                tickLine={false}
                axisLine={{ stroke: "#27272A" }}
                allowDecimals={false}
              />
              <Tooltip
                cursor={{ fill: "rgba(194,83,59,0.06)" }}
                contentStyle={{
                  background: "#050505",
                  border: "1px solid #27272A",
                  borderRadius: 0,
                  fontSize: 12,
                  fontFamily: "Outfit, sans-serif",
                  color: "#FAFAFA",
                }}
                labelStyle={{ color: "#71717A" }}
              />
              <Legend
                wrapperStyle={{ fontSize: 11, fontFamily: "Outfit, sans-serif", paddingTop: 12 }}
                iconType="square"
              />
              <Bar dataKey="Leads" fill={ACCENT} />
              <Bar dataKey="Contacts" fill="#FAFAFA" />
              <Bar dataKey="Applications" fill="#71717A" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      {/* Lists */}
      <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#27272A] border border-[#27272A]">
        <motion.div {...fadeUp} className="bg-[#0A0A0C] p-8" data-testid="top-channels">
          <p className="overline mb-6">Top channels</p>
          {!data.top_channels || data.top_channels.length === 0 ? (
            <p className="text-zinc-500 text-sm">No channel data yet.</p>
          ) : (
            <ul className="space-y-3">
              {data.top_channels.map((c, i) => (
                <li key={c.label} className="flex items-center gap-4">
                  <span className="font-serif-display italic text-xl w-8" style={{ color: ACCENT }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="flex-1 text-zinc-300 text-sm capitalize">{c.label}</span>
                  <span className="text-white text-sm font-medium tabular-nums">{c.count}</span>
                </li>
              ))}
            </ul>
          )}
        </motion.div>

        <motion.div {...fadeUp} className="bg-[#0A0A0C] p-8" data-testid="top-services">
          <p className="overline mb-6">Top services requested</p>
          {data.top_services.length === 0 ? (
            <p className="text-zinc-500 text-sm">No service preferences captured yet.</p>
          ) : (
            <ul className="space-y-3">
              {data.top_services.map((s, i) => (
                <li key={s.label} className="flex items-center gap-4">
                  <span className="font-serif-display italic text-xl w-8" style={{ color: ACCENT }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="flex-1 text-zinc-300 text-sm">{s.label}</span>
                  <span className="text-white text-sm font-medium tabular-nums">{s.count}</span>
                </li>
              ))}
            </ul>
          )}
        </motion.div>

        <motion.div {...fadeUp} className="bg-[#0A0A0C] p-8" data-testid="top-roles">
          <p className="overline mb-6">Lead-magnet roles</p>
          {data.top_lead_roles.length === 0 ? (
            <p className="text-zinc-500 text-sm">No roles captured yet.</p>
          ) : (
            <ul className="space-y-3">
              {data.top_lead_roles.map((r, i) => (
                <li key={r.label} className="flex items-center gap-4">
                  <span className="font-serif-display italic text-xl w-8" style={{ color: ACCENT }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="flex-1 text-zinc-300 text-sm">{r.label}</span>
                  <span className="text-white text-sm font-medium tabular-nums">{r.count}</span>
                </li>
              ))}
            </ul>
          )}
        </motion.div>
      </div>

      <p className="text-zinc-600 text-xs mt-12 leading-relaxed max-w-2xl">
        Conversion measures the percentage of lead-magnet downloaders who submitted the contact form
        within seven calendar days of their download, matched by email address. Weekly buckets begin
        on Monday 00:00 UTC.
      </p>
    </div>
  );
}
